import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { reviewsAPI } from '../../services/api';
import ScheduleReviewModal from '../../components/employer/ScheduleReviewModal';
import { toast } from 'sonner';
import { 
  Calendar, Plus, Loader2, ChevronLeft, ChevronRight, User,
  Clock, RefreshCw, CalendarCheck, FileText, Check, X
} from 'lucide-react';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const localizer = momentLocalizer(moment);

// Color mapping for review types
const REVIEW_COLORS = {
  'one-time': { bg: '#3B82F6', light: 'rgba(59, 130, 246, 0.2)', border: '#3B82F6' },
  'weekly': { bg: '#22C55E', light: 'rgba(34, 197, 94, 0.2)', border: '#22C55E' },
  'quarterly': { bg: '#F59E0B', light: 'rgba(245, 158, 11, 0.2)', border: '#F59E0B' },
  'annual': { bg: '#EF4444', light: 'rgba(239, 68, 68, 0.2)', border: '#EF4444' },
};

const STATUS_COLORS = {
  scheduled: { text: 'text-blue-400', bg: 'bg-blue-500/20' },
  overdue: { text: 'text-red-400', bg: 'bg-red-500/20' },
  completed: { text: 'text-green-400', bg: 'bg-green-500/20' },
  cancelled: { text: 'text-[#5B6472]', bg: 'bg-[#F5F8FF]' },
};

const ReviewCalendar = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventDetail, setShowEventDetail] = useState(false);

  const fetchCalendarData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reviewsAPI.getCalendarReviews();
      const calendarEvents = (res.data || []).map(review => ({
        id: review.id,
        title: review.employee_name || 'Employee',
        start: new Date(review.due_date),
        end: new Date(review.due_date),
        resource: review,
      }));
      setEvents(calendarEvents);
    } catch (error) {
      console.error('Error fetching calendar data:', error);
      toast.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCalendarData();
  }, [fetchCalendarData]);

  const eventStyleGetter = useCallback((event) => {
    const reviewType = event.resource?.review_type || 'one-time';
    const status = event.resource?.status || 'scheduled';
    const colors = REVIEW_COLORS[reviewType] || REVIEW_COLORS['one-time'];
    
    let backgroundColor = colors.bg;
    let opacity = 1;
    
    if (status === 'completed') {
      opacity = 0.6;
    } else if (status === 'cancelled') {
      backgroundColor = '#4A4A6A';
      opacity = 0.5;
    } else if (status === 'overdue') {
      backgroundColor = '#EF4444';
    }
    
    return {
      style: {
        backgroundColor,
        borderRadius: '6px',
        border: `2px solid ${colors.border}`,
        opacity,
        color: 'white',
        padding: '2px 6px',
        fontSize: '12px',
        fontWeight: 500,
      },
    };
  }, []);

  const handleSelectEvent = useCallback((event) => {
    setSelectedEvent(event.resource);
    setShowEventDetail(true);
  }, []);

  const handleNavigate = useCallback((newDate) => {
    setDate(newDate);
  }, []);

  const handleViewChange = useCallback((newView) => {
    setView(newView);
  }, []);

  const handleCompleteReview = async (reviewId) => {
    try {
      const res = await reviewsAPI.completeScheduledReview(reviewId);
      toast.success('Review marked as complete!');
      if (res.data.next_review) {
        toast.info(`Next review auto-scheduled for ${new Date(res.data.next_review.due_date).toLocaleDateString()}`);
      }
      setShowEventDetail(false);
      fetchCalendarData();
    } catch (error) {
      toast.error('Failed to complete review');
    }
  };

  const handleCancelReview = async (reviewId) => {
    try {
      await reviewsAPI.cancelScheduledReview(reviewId);
      toast.success('Review cancelled');
      setShowEventDetail(false);
      fetchCalendarData();
    } catch (error) {
      toast.error('Failed to cancel review');
    }
  };

  // Custom toolbar
  const CustomToolbar = useMemo(() => {
    return ({ label, onNavigate, onView, view: currentView }) => (
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onNavigate('PREV')}
            className="text-[#5B6472] hover:text-[#0A0F1E] hover:bg-[#F5F8FF]"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onNavigate('TODAY')}
            className="text-[#5B6472] hover:text-[#0A0F1E] hover:bg-[#F5F8FF]"
          >
            Today
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onNavigate('NEXT')}
            className="text-[#5B6472] hover:text-[#0A0F1E] hover:bg-[#F5F8FF]"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
          <span className="text-lg font-semibold text-[#0A0F1E] ml-2">{label}</span>
        </div>
        
        <div className="flex items-center gap-2">
          <div className="bg-[#F5F8FF] rounded-xl p-1 flex gap-1">
            {['month', 'week', 'day', 'agenda'].map((v) => (
              <Button
                key={v}
                variant={currentView === v ? 'default' : 'ghost'}
                size="sm"
                onClick={() => onView(v)}
                className={`rounded-lg capitalize ${
                  currentView === v 
                    ? 'bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF]' 
                    : 'text-[#5B6472] hover:text-[#0A0F1E] hover:bg-[#F5F8FF]'
                }`}
              >
                {v}
              </Button>
            ))}
          </div>
        </div>
      </div>
    );
  }, []);

  // Legend component
  const Legend = () => (
    <div className="flex flex-wrap gap-4 mb-4">
      {Object.entries(REVIEW_COLORS).map(([type, colors]) => (
        <div key={type} className="flex items-center gap-2">
          <div 
            className="w-4 h-4 rounded" 
            style={{ backgroundColor: colors.bg }}
          />
          <span className="text-[#5B6472] text-sm capitalize">{type.replace('-', ' ')}</span>
        </div>
      ))}
    </div>
  );

  // Event detail sidebar
  const EventDetailPanel = () => {
    if (!selectedEvent) return null;
    
    const colors = REVIEW_COLORS[selectedEvent.review_type] || REVIEW_COLORS['one-time'];
    const statusColors = STATUS_COLORS[selectedEvent.status] || STATUS_COLORS.scheduled;
    const isPast = new Date(selectedEvent.due_date) < new Date();
    const canComplete = selectedEvent.status === 'scheduled' || selectedEvent.status === 'overdue';
    
    return (
      <div 
        className={`fixed inset-y-0 right-0 w-80 bg-[#FFFFFF] border-l border-[#E8EDF5] p-6 transform transition-transform z-50 overflow-y-auto ${
          showEventDetail ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-[#0A0F1E]">Review Details</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowEventDetail(false)}
            className="text-[#5B6472] hover:text-[#0A0F1E]"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        {/* Employee Info */}
        <div className="flex items-center gap-3 mb-6">
          {selectedEvent.employee_photo ? (
            <img src={selectedEvent.employee_photo} alt="" className="w-12 h-12 rounded-full object-cover" />
          ) : (
            <div className="w-12 h-12 rounded-full bg-[#F5F8FF] flex items-center justify-center">
              <User className="w-6 h-6 text-[#5B6472]" />
            </div>
          )}
          <div>
            <p className="text-[#0A0F1E] font-medium">{selectedEvent.employee_name || 'Employee'}</p>
            <p className="text-[#5B6472] text-sm">{selectedEvent.employee_title || 'No title'}</p>
          </div>
        </div>
        
        {/* Review Type */}
        <div className="p-4 rounded-xl mb-4" style={{ backgroundColor: colors.light }}>
          <div className="flex items-center gap-2">
            {selectedEvent.review_type === 'one-time' && <Calendar className="w-4 h-4" style={{ color: colors.bg }} />}
            {selectedEvent.review_type === 'weekly' && <RefreshCw className="w-4 h-4" style={{ color: colors.bg }} />}
            {selectedEvent.review_type === 'quarterly' && <CalendarCheck className="w-4 h-4" style={{ color: colors.bg }} />}
            {selectedEvent.review_type === 'annual' && <FileText className="w-4 h-4" style={{ color: colors.bg }} />}
            <span className="capitalize font-medium" style={{ color: colors.bg }}>
              {selectedEvent.review_type?.replace('-', ' ')} Review
            </span>
          </div>
        </div>
        
        {/* Details */}
        <div className="space-y-4">
          <div>
            <p className="text-[#5B6472] text-sm">Status</p>
            <span className={`px-3 py-1 rounded-full text-sm ${statusColors.bg} ${statusColors.text} inline-block mt-1`}>
              {selectedEvent.status?.charAt(0).toUpperCase() + selectedEvent.status?.slice(1)}
            </span>
          </div>
          
          <div>
            <p className="text-[#5B6472] text-sm">Date & Time</p>
            <p className="text-[#0A0F1E]">
              {new Date(selectedEvent.due_date).toLocaleDateString('en-US', {
                weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
              })}
            </p>
            <p className="text-[#5B6472] text-sm">
              {selectedEvent.scheduled_time || '09:00'}
            </p>
          </div>
          
          {selectedEvent.focus_areas && (
            <div>
              <p className="text-[#5B6472] text-sm">Focus Areas</p>
              <p className="text-[#0A0F1E]">{selectedEvent.focus_areas}</p>
            </div>
          )}
          
          {selectedEvent.notes && (
            <div>
              <p className="text-[#5B6472] text-sm">Notes</p>
              <p className="text-[#0A0F1E]">{selectedEvent.notes}</p>
            </div>
          )}
        </div>
        
        {/* Actions */}
        <div className="mt-6 space-y-2">
          {canComplete && (
            <>
              <Button
                onClick={() => handleCompleteReview(selectedEvent.id)}
                className="w-full bg-green-500 hover:bg-green-600 hover:bg-green-700 text-[#FFFFFF] rounded-xl"
              >
                <Check className="w-4 h-4 mr-2" />
                Mark Complete
              </Button>
              <Button
                onClick={() => navigate(`/employer/reviews/new?employee_id=${selectedEvent.employee_id}&scheduled_id=${selectedEvent.id}`)}
                className="w-full bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl"
              >
                <FileText className="w-4 h-4 mr-2" />
                Create Review
              </Button>
              <Button
                variant="outline"
                onClick={() => handleCancelReview(selectedEvent.id)}
                className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-xl"
              >
                Cancel Review
              </Button>
            </>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="review-calendar">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fade-in">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0F1E]">Review Calendar</h1>
            <p className="mt-2 text-[#5B6472]">View and manage all scheduled performance reviews</p>
          </div>
          <Button
            onClick={() => setShowScheduleModal(true)}
            className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl"
            data-testid="schedule-review-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Schedule Review
          </Button>
        </div>
        
        {/* Legend */}
        <Legend />
        
        {/* Calendar */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4 sm:p-6 animate-fade-in">
          <style>{`
            .rbc-calendar {
              background: transparent;
              color: #F5F5F7;
            }
            .rbc-toolbar {
              display: none;
            }
            .rbc-header {
              background: #F5F8FF;
              color: #5B6472;
              padding: 8px;
              border-bottom: 1px solid #E8EDF5 !important;
              font-weight: 500;
            }
            .rbc-month-view, .rbc-time-view, .rbc-agenda-view {
              border: 1px solid #E8EDF5;
              border-radius: 12px;
              overflow: hidden;
            }
            .rbc-month-row {
              border-bottom: 1px solid #E8EDF5;
            }
            .rbc-day-bg {
              background: #FFFFFF;
            }
            .rbc-day-bg + .rbc-day-bg {
              border-left: 1px solid #E8EDF5;
            }
            .rbc-off-range-bg {
              background: #FFFFFF;
              opacity: 0.5;
            }
            .rbc-today {
              background: rgba(233, 69, 96, 0.1) !important;
            }
            .rbc-date-cell {
              color: #5B6472;
              padding: 4px 8px;
            }
            .rbc-date-cell.rbc-now {
              color: #1B5AFF;
              font-weight: 600;
            }
            .rbc-event {
              cursor: pointer;
            }
            .rbc-event:focus {
              outline: none;
            }
            .rbc-show-more {
              color: #1B5AFF;
              font-weight: 500;
            }
            .rbc-time-content {
              border-top: 1px solid #E8EDF5;
            }
            .rbc-time-slot {
              border-top: 1px solid #E8EDF5;
            }
            .rbc-timeslot-group {
              border-bottom: 1px solid #E8EDF5;
            }
            .rbc-time-header-content {
              border-left: 1px solid #E8EDF5;
            }
            .rbc-time-gutter {
              color: #5B6472;
            }
            .rbc-agenda-date-cell, .rbc-agenda-time-cell {
              color: #5B6472;
              padding: 12px;
            }
            .rbc-agenda-event-cell {
              padding: 12px;
            }
            .rbc-agenda-table {
              border-collapse: collapse;
            }
            .rbc-agenda-table tbody > tr > td + td {
              border-left: 1px solid #E8EDF5;
            }
            .rbc-agenda-table tbody > tr + tr {
              border-top: 1px solid #E8EDF5;
            }
          `}</style>
          
          <BigCalendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 600 }}
            date={date}
            view={view}
            onNavigate={handleNavigate}
            onView={handleViewChange}
            onSelectEvent={handleSelectEvent}
            eventPropGetter={eventStyleGetter}
            components={{
              toolbar: CustomToolbar,
            }}
            popup
            selectable
          />
        </Card>
        
        {/* Event Detail Panel */}
        <EventDetailPanel />
        
        {/* Overlay for detail panel */}
        {showEventDetail && (
          <div 
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setShowEventDetail(false)}
          />
        )}
        
        {/* Schedule Modal */}
        <ScheduleReviewModal
          open={showScheduleModal}
          onClose={() => setShowScheduleModal(false)}
          onSuccess={fetchCalendarData}
        />
      </div>
    </DashboardLayout>
  );
};

export default ReviewCalendar;
